import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { User } from '../../models/User';
import { formatDate } from '../../utils/DateFormatter';

interface UserTableProps {
  users: User[];
  isAdmin: boolean;
  isContactsLoaded: boolean;
  searchQuery: string;
}

export const UserTable = (props: UserTableProps) => {
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filteredAdvisors, setFilteredAdvisors] = useState<User[]>([]);

  useEffect(() => {
    if (!isEmpty(props.users)) {
      const lowerCaseQuery = props.searchQuery.toLowerCase();

      const filterAndSort = (userList: User[]) => {
        return userList
          .filter((user) => {
            if (isEmpty(lowerCaseQuery)) return true;
            return (
              (user.firstName || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.lastName || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.email || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.city || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.stateText || '').toLowerCase().includes(lowerCaseQuery)
            );
          })
          .sort((a, b) => a.firstName.localeCompare(b.firstName));
      };

      setFilteredUsers(filterAndSort(props.users.filter((user) => !user.advisor)));
      setFilteredAdvisors(filterAndSort(props.users.filter((user) => user.advisor)));
    }
  }, [props.users, props.searchQuery]);

  const formatName = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`;
  };

  const formatCellNumber = (cellNumber: string): string => {
    // Ensure cellNumber is a string before checking length
    const numStr = String(cellNumber);
    return numStr.length === 10
      ? `${numStr.substring(0, 3)}-${numStr.substring(3, 6)}-${numStr.substring(6, 10)}`
      : numStr;
  };

  const formatEmail = (email: string) => {
    return (
      <a className={email !== 'N/A' ? 'dark:text-blue-300 text-blue-700' : ''} href={`mailto:${email}`}>
        {email}
      </a>
    );
  };

  const formatAddress = (streetAddress: string, city: string, stateText: string, zipCode: string) => {
    return (
      <a
        className="dark:text-blue-300 text-blue-700"
        target="_blank"
        href={`https://www.google.com/maps/search/?api=1&query=${streetAddress}+${city}+${stateText}+${zipCode}`}
      >{`${streetAddress}, ${city} ${stateText}, ${zipCode}`}</a>
    );
  };

  // Helper function to render family contacts section
  const renderFamilyContacts = () => {
    if (isEmpty(filteredUsers) && isEmpty(filteredAdvisors) && !isEmpty(props.searchQuery)) {
      return <div className="mb-6 text-slate-500 dark:text-slate-400 font-medium">No family contacts found matching "{props.searchQuery}"</div>;
    } else if (isEmpty(filteredUsers) && isEmpty(props.searchQuery)) {
      return <div className="mb-6 text-slate-500 dark:text-slate-400 font-medium">No family contacts found</div>;
    } else {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {filteredUsers.map((user, index) => (
            <div key={'User' + index} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-lg sm:text-xl font-bold dark:text-white text-slate-900 mb-4">{formatName(user.firstName, user.lastName)}</h3>
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p className="flex items-center"><span className="font-medium w-20">Email:</span> <span className="hover:underline truncate">{formatEmail(user.email)}</span></p>
                <p className="flex items-center"><span className="font-medium w-20">Cell:</span> <span>{formatCellNumber(user.cellNumber)}</span></p>
                <p className="flex items-center"><span className="font-medium w-20">Birthday:</span> <span>{formatDate(user.birthday)}</span></p>
                <p className="flex items-start"><span className="font-medium w-20 mt-0.5">Address:</span> <span className="hover:underline flex-1">{formatAddress(user.streetAddress, user.city, user.stateText, user.zipCode)}</span></p>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  // Helper function to render advisor contacts section
  const renderAdvisorContacts = () => {
    if (isEmpty(filteredAdvisors) && !isEmpty(props.searchQuery)) {
      return <div className="text-slate-500 dark:text-slate-400 font-medium">No advisor contacts found matching "{props.searchQuery}"</div>;
    } else if (isEmpty(filteredAdvisors) && isEmpty(props.searchQuery)) {
      return <div className="text-slate-500 dark:text-slate-400 font-medium">No advisor contacts found</div>;
    } else {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAdvisors.map((advisor, index) => (
            <div key={'Advisor' + index} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow">
              <h3 className="text-lg sm:text-xl font-bold dark:text-white text-slate-900 mb-4">{formatName(advisor.firstName, advisor.lastName)}</h3>
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p className="flex items-center"><span className="font-medium w-20">Email:</span> <span className="hover:underline truncate">{formatEmail(advisor.email)}</span></p>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <>
      {!props.isContactsLoaded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={'loading-user-' + i} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 animate-pulse">
              <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      ) : ( // If contacts are loaded, render the actual content
        <>
          {renderFamilyContacts()}

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mt-6 mb-6 dark:text-white text-slate-900">
            Advisor Contacts
          </h1>
          {renderAdvisorContacts()}
        </>
      )}
    </>
  );
};
